import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spinner, Modal } from 'react-bootstrap';
import * as pdfjsLib from 'pdfjs-dist';
import JSZip from 'jszip';
import {
  getSigningData,
  submitSigning,
  declineSigning,
  getSigningDocumentFile,
  getSigningDocumentFileBatchZipUrls,
  extractSigningPayloadData,
  isAlreadySignedPayload,
} from '../../services/signingService';
import { isValidEmailAddress } from '../../utils/emailValidation';
import {
  formatDateUTC,
  formatDateTimeUTC,
  isValidDateDDMMYYYY,
  normalizeToDateDDMMYYYY,
  normalizeToDateISO,
} from '../../utils/dateFormat';
import logo from '../../assets/images/web-logo-01 (1).png';
import {
  prepareReadOnlyValueHtml,
  readOnlyPresentationToFlexStyle,
  readOnlyPresentationToInlineStyle,
  resolveReadOnlyFieldPresentation,
  unwrapReadOnlyFieldValueWrapper,
  wrapReadOnlyFieldTypographyHtml,
} from '../../utils/readOnlyRichText';
import { isHiddenFieldType } from '../../utils/fieldTypes';
import '../../assets/scss/components/_sign-document.scss';

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;

function signingFieldValueHasHtmlMarkup(value) {
  return /<[^>]+>/.test(String(value || ''));
}

/**
 * Editable, formatted text field for signing. The signer can edit the text inline
 * while the sender's formatting is preserved (no toolbar). It is uncontrolled: the
 * initial HTML is written to the DOM once so typing never resets the caret.
 */
function SigningInlineRichField({ initialHtml, className, style, onChangeHtml, onBlurHtml }) {
  const hostRef = useRef(null);
  useEffect(() => {
    if (hostRef.current) {
      hostRef.current.innerHTML = initialHtml || '';
    }
    // Initialize once on mount only; later edits must not reset the caret.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div
      ref={hostRef}
      className={className}
      style={style}
      contentEditable
      suppressContentEditableWarning
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onInput={() => onChangeHtml?.(hostRef.current?.innerHTML || '')}
      onBlur={() => onBlurHtml?.(hostRef.current?.innerHTML || '')}
    />
  );
}

function isCheckboxFieldType(fieldType) {
  return String(fieldType || '').toLowerCase() === 'checkbox';
}

function isSigningFieldRequired(field) {
  if (!field) return false;
  const v = field.is_required;
  if (v === false || v === 0 || v === '0') return false;
  return Boolean(v);
}

function isCheckboxFieldChecked(rawValue) {
  const valueNorm = String(rawValue ?? '').trim().toLowerCase();
  return valueNorm === 'true' || valueNorm === 'selected' || valueNorm === '1' || valueNorm === 'yes';
}

const SignDocument = () => {
  const { recipientId: envelopeRecipientId } = useParams();
  const navigate = useNavigate();

  // Session
  const signingToken = sessionStorage.getItem('signing_token') || '';
  const redirectToOtpWithError = useCallback((message) => {
    const authMessage = String(message || 'Invalid or expired signing token. Please verify OTP again.');
    sessionStorage.removeItem('signing_token');
    sessionStorage.removeItem('signing_session');
    sessionStorage.setItem('signing_auth_error', authMessage);
    navigate(`/sign/${envelopeRecipientId}`, { replace: true });
  }, [navigate, envelopeRecipientId]);

  const showAlreadySignedScreen = useCallback((payload) => {
    const data = extractSigningPayloadData(payload) || {};
    setAlreadySigned(true);
    setAlreadySignedData({
      signed_at: data.signed_at,
      envelope: data.envelope,
      subject:
        data.envelope?.subject
        || data.subject
        || data.envelope_subject
        || null,
      message: typeof payload === 'object' ? payload?.message : null,
    });
    setError('');
    setLoading(false);
  }, []);

  // Loading & status states
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingStatus, setLoadingStatus] = useState('Preparing signing session...');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [fieldNavError, setFieldNavError] = useState('');
  const [completed, setCompleted] = useState(false);
  const [completionData, setCompletionData] = useState(null);
  const [alreadySigned, setAlreadySigned] = useState(false);
  const [alreadySignedData, setAlreadySignedData] = useState(null);
  const [declined, setDeclined] = useState(false);
  const [declineData, setDeclineData] = useState(null);

  // Signing data from API
  const [signingData, setSigningData] = useState(null);
  const [fields, setFields] = useState([]);
  const [existingSignatures, setExistingSignatures] = useState([]);

  // Field values filled by the user: { field_{id}: value }
  const [fieldValues, setFieldValues] = useState({});

  // Active field (for the input modal)
  const [activeField, setActiveField] = useState(null);

  // Signature modal tabs: 'draw' | 'upload' | 'adopt'
  const [sigTab, setSigTab] = useState('draw');
  const [adoptName, setAdoptName] = useState('');
  const [adoptStyles, setAdoptStyles] = useState([]);
  const [selectedAdopt, setSelectedAdopt] = useState(null);
  const [uploadedSigDataUrl, setUploadedSigDataUrl] = useState(null);
  const [signatureChoiceField, setSignatureChoiceField] = useState(null);
  const [suggestedSignatureValue, setSuggestedSignatureValue] = useState('');

  // Signee IP
  const [signeeIp, setSigneeIp] = useState('');

  const sortDocumentsByOrder = useCallback((documents) => {
    if (!Array.isArray(documents)) return [];
    return documents
      .map((doc, index) => ({ doc, index }))
      .sort((a, b) => {
        const aOrder = Number(a.doc?.order);
        const bOrder = Number(b.doc?.order);
        const aHasOrder = Number.isFinite(aOrder);
        const bHasOrder = Number.isFinite(bOrder);
        if (aHasOrder && bHasOrder) {
          if (aOrder !== bOrder) return aOrder - bOrder;
          return a.index - b.index;
        }
        if (aHasOrder) return -1;
        if (bHasOrder) return 1;
        return a.index - b.index;
      })
      .map((item) => item.doc);
  }, []);

  useEffect(() => {
    fetch('https://api.ipify.org?format=json')
      .then((r) => r.json())
      .then((data) => setSigneeIp(data.ip || ''))
      .catch(() => setSigneeIp(''));
  }, []);

  // Decline modal
  const [showDecline, setShowDecline] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [declining, setDeclining] = useState(false);
  const [fieldsPanelOpen, setFieldsPanelOpen] = useState(false);

  // SVG document state (same approach as CreateEnvelope)
  const [mergedSvg, setMergedSvg] = useState('');
  const [svgPageHeights, setSvgPageHeights] = useState([]);
  const [svgMaxWidth, setSvgMaxWidth] = useState(0);
  const [svgTotalHeight, setSvgTotalHeight] = useState(0);
  const [zoomLevel, setZoomLevel] = useState('fitToWidth');
  const [viewerSize, setViewerSize] = useState({ width: 0, height: 0 });
  const [, setCurrentPage] = useState(1);
  const [, setTotalPages] = useState(0);

  // Refs
  const svgContainerRef = useRef(null);
  const pageContainerRef = useRef(null);
  const completeBtnRef = useRef(null);
  const signatureCanvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const strokesRef = useRef([]); // Array of stroke point arrays
  const activeStrokeRef = useRef(null); // Current stroke points

  useEffect(() => {
    const viewer = svgContainerRef.current?.closest('.sign-document-viewer');
    if (!viewer) return undefined;

    const measureViewer = () => {
      const width = viewer.clientWidth || 0;
      const height = viewer.clientHeight || 0;
      const styles = window.getComputedStyle(viewer);
      const paddingX = (parseFloat(styles.paddingLeft) || 0) + (parseFloat(styles.paddingRight) || 0);
      const contentWidth = Math.max(width - paddingX, 0);
      // Fit-to-width only depends on inner width. Ignore height-only changes (mobile URL bar)
      // so a re-render does not cancel the in-progress touch scroll.
      setViewerSize((prev) => (
        prev.width === contentWidth ? prev : { width: contentWidth, height }
      ));
    };

    // Ensure first paint gets a real measured width (fixes iPhone initial 100% scale).
    measureViewer();
    const rafId = window.requestAnimationFrame(measureViewer);

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(measureViewer);
      resizeObserver.observe(viewer);
    }

    window.addEventListener('resize', measureViewer);
    window.addEventListener('orientationchange', measureViewer);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', measureViewer);
      window.removeEventListener('orientationchange', measureViewer);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [mergedSvg]);

  // ─── Zoom / scale calculation ───────────────────────────────────────────
  // "Fit to Page" has been removed from zoom options; keep a safe fallback for legacy values.
  const getCurrentScale = useCallback(() => {
    if (zoomLevel === 'fitToWidth') {
      if (viewerSize.width > 0 && svgMaxWidth > 0) {
        return viewerSize.width / svgMaxWidth;
      }
      return 1;
    }
    if (zoomLevel === 'fitToPage') return 1;
    return parseInt(zoomLevel) / 100;
  }, [zoomLevel, svgMaxWidth, viewerSize.width]);

  const documentIndexById = useMemo(() => {
    const map = new Map();
    const docs = Array.isArray(signingData?.documents) ? signingData.documents : [];
    docs.forEach((doc, idx) => {
      const ids = [doc?.original_document_id, doc?.id, doc?.document_id]
        .map((value) => String(value ?? '').trim())
        .filter(Boolean);
      ids.forEach((id) => {
        if (!map.has(id)) map.set(id, idx);
      });
    });
    return map;
  }, [signingData]);

  // Resolve field page position using (document_id + page_number) first.
  // Fallback to global merged-page indexing for backward compatibility.
  const resolveFieldPageInfo = useCallback((field) => {
    if (!field || !svgPageHeights.length) return null;

    const requestedPageRaw = Number(field.page_number);
    const requestedPage = Number.isFinite(requestedPageRaw) && requestedPageRaw > 0
      ? Math.floor(requestedPageRaw)
      : 1;

    const fieldDocId = String(field.document_id ?? field.original_document_id ?? '').trim();
    if (fieldDocId) {
      const docIndex = documentIndexById.get(fieldDocId);
      if (docIndex !== undefined) {
        const exactMatch = svgPageHeights.find((page) => (
          Number(page.documentIndex) === Number(docIndex)
          && Number(page.pageNumber) === requestedPage
        ));
        if (exactMatch) return exactMatch;

        const firstDocPage = svgPageHeights.find(
          (page) => Number(page.documentIndex) === Number(docIndex)
        );
        if (firstDocPage) return firstDocPage;
      }
    }

    const pageIndex = Math.min(Math.max(requestedPage - 1, 0), svgPageHeights.length - 1);
    return svgPageHeights[pageIndex] || null;
  }, [svgPageHeights, documentIndexById]);

  // ─── Convert PDF page to SVG (same as CreateEnvelope) ───────────────────
  const convertPageToSvg = async (page, viewport) => {
    const renderScale = 2;
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width * renderScale;
    canvas.height = viewport.height * renderScale;
    const ctx = canvas.getContext('2d');
    const scaledViewport = page.getViewport({ scale: viewport.scale * renderScale });
    await page.render({ canvasContext: ctx, viewport: scaledViewport }).promise;
    const dataUrl = canvas.toDataURL('image/png', 1.0);
    return `<svg width="${viewport.width}" height="${viewport.height}" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
      <image x="0" y="0" width="${viewport.width}" height="${viewport.height}" xlink:href="${dataUrl}" preserveAspectRatio="xMidYMid meet" style="image-rendering: auto;"/>
    </svg>`;
  };

  const normalizeDocumentKey = (id) => String(id ?? '').trim();

  const inferDocumentKeyFromFilename = (name) => {
    const raw = String(name || '').trim();
    if (!raw) return '';
    const matches = raw.match(/\d+/g);
    if (!matches || matches.length === 0) return '';
    return String(matches[matches.length - 1]);
  };

  const extractPdfBuffersFromZipItem = async (zipItem, requestedDocKeysSet, expectedDocKeys = []) => {
    const zipUrl = String(zipItem?.zip_url || zipItem?.temporary_url || '').trim();
    if (!zipUrl) return new Map();

    const batchDocKeys = (
      Array.isArray(zipItem?.document_ids) ? zipItem.document_ids
        : Array.isArray(zipItem?.documents) ? zipItem.documents.map((d) => d?.document_id ?? d?.id)
          : []
    )
      .map((id) => normalizeDocumentKey(id))
      .filter(Boolean);

    const fallbackDocKeys = (Array.isArray(expectedDocKeys) ? expectedDocKeys : [])
      .map((id) => normalizeDocumentKey(id))
      .filter(Boolean);

    const normalizeZipEntryName = (value) => (
      String(value || '')
        .trim()
        .replace(/\\/g, '/')
        .toLowerCase()
    );

    const filenameDocKeyMap = new Map();
    if (Array.isArray(zipItem?.documents)) {
      zipItem.documents.forEach((doc) => {
        const docKey = normalizeDocumentKey(doc?.document_id ?? doc?.id);
        const fileNameRaw = String(doc?.file_name || doc?.filename || doc?.name || '').trim();
        if (docKey && fileNameRaw) {
          const normalizedFullName = normalizeZipEntryName(fileNameRaw);
          const normalizedBaseName = normalizedFullName.split('/').pop() || normalizedFullName;
          filenameDocKeyMap.set(normalizedFullName, docKey);
          filenameDocKeyMap.set(normalizedBaseName, docKey);
        }
      });
    }
    const hasExplicitFilenameMapping = filenameDocKeyMap.size > 0;

    const zipRes = await fetch(zipUrl);
    if (!zipRes.ok) {
      throw new Error(`Batch ZIP fetch failed with status ${zipRes.status}`);
    }

    const zipBytes = await zipRes.arrayBuffer();
    const zip = await JSZip.loadAsync(zipBytes);
    const entries = Object.values(zip.files).filter((entry) => !entry.dir && /\.pdf$/i.test(entry.name));

    const buffersByDocKey = new Map();
    for (let i = 0; i < entries.length; i += 1) {
      const entry = entries[i];
      const normalizedEntryFullName = normalizeZipEntryName(entry.name);
      const normalizedEntryBaseName = normalizedEntryFullName.split('/').pop() || normalizedEntryFullName;
      const explicitMappedDocKey =
        filenameDocKeyMap.get(normalizedEntryFullName)
        ?? filenameDocKeyMap.get(normalizedEntryBaseName)
        ?? null;

      const candidateDocKeys = (hasExplicitFilenameMapping
        ? [explicitMappedDocKey]
        : [
            explicitMappedDocKey,
            inferDocumentKeyFromFilename(normalizedEntryBaseName),
            batchDocKeys[i],
            fallbackDocKeys[i],
          ])
        .map((id) => normalizeDocumentKey(id))
        .filter(Boolean);

      const docKey = candidateDocKeys.find((id) => requestedDocKeysSet.has(id) && !buffersByDocKey.has(id)) || null;
      if (!docKey) continue;

      const bytes = await entry.async('arraybuffer');
      buffersByDocKey.set(docKey, bytes);
    }

    return buffersByDocKey;
  };

  // ─── Load signing data & PDFs ───────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      if (!envelopeRecipientId || !signingToken) {
        // No token — redirect back to OTP
        navigate(`/sign/${envelopeRecipientId}`, { replace: true });
        return;
      }

      try {
        setLoadingProgress(0);
        setLoadingStatus('Preparing recipient details...');

        // Fetch signing data
        const response = await getSigningData(envelopeRecipientId, signingToken);
        if (isAlreadySignedPayload(response)) {
          showAlreadySignedScreen(response);
          return;
        }
        if (response?.success === false) {
          setError(response?.message || 'Failed to load signing data.');
          return;
        }
        const data = extractSigningPayloadData(response);
        if (!data) {
          setError('Failed to load signing data.');
          return;
        }
        if (isAlreadySignedPayload(data)) {
          showAlreadySignedScreen(data);
          return;
        }
        const sortedDocuments = sortDocumentsByOrder(data.documents || []);
        setSigningData({
          ...data,
          documents: sortedDocuments,
        });
        setLoadingProgress(20);

        setLoadingStatus('Preparing fields...');
        setFields(data.fields || []);
        setExistingSignatures(data.existing_signatures || []);
        setLoadingProgress(40);

        // Pre-fill field values for previously filled fields and auto-fill dates
        const today = formatDateUTC(new Date());
        const preValues = {};
        (data.fields || []).forEach((f) => {
          // Capture value from any available source
          const existingValue = f.field_value || f.value || f.default_value;
          if (existingValue) {
            preValues[`field_${f.id}`] = isDateFieldType(f.field_type)
              ? (normalizeToDateDDMMYYYY(existingValue) || existingValue)
              : existingValue;
          }
          // Auto-fill dateSigned fields with current date
          if (f.field_type === 'dateSigned' && !existingValue && !f.filled_by_previous_signee) {
            preValues[`field_${f.id}`] = today;
          }
        });
        setFieldValues(preValues);

        // Load PDFs and convert to SVG
        const documents = sortedDocuments;
        if (documents.length === 0) {
          setError('No documents found for signing.');
          setLoading(false);
          return;
        }

        setLoadingStatus('Preparing documents...');
        await loadDocumentPDFs(documents, (documentsRatio, statusText) => {
          const safeRatio = Math.max(0, Math.min(1, Number(documentsRatio) || 0));
          const weighted = 40 + Math.round(safeRatio * 60);
          setLoadingProgress(weighted);
          if (statusText) setLoadingStatus(statusText);
        });
        setLoadingProgress(100);
        setLoadingStatus('Ready.');
      } catch (err) {
        const errorPayload = err?.response?.data;
        const msg = errorPayload?.message || err?.message || 'Failed to load signing data.';
        if (isAlreadySignedPayload(errorPayload) || isAlreadySignedPayload(msg)) {
          showAlreadySignedScreen(errorPayload || { message: msg });
          return;
        }
        if (err?.response?.status === 401) {
          // Token expired — go back to OTP and surface backend message.
          redirectToOtpWithError(msg);
          return;
        }
        setError(msg);
      } finally {
        setLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envelopeRecipientId, signingToken, redirectToOtpWithError, sortDocumentsByOrder]);

  // Load PDFs → SVG merge (extracted from CreateEnvelope)
  const loadDocumentPDFs = async (documents, onProgress = null) => {
    const allSvgPages = [];
    const pageHeights = [];
    let totalPageCount = 0;
    let processedDocuments = 0;
    const pdfBuffersByDocumentKey = new Map();

    if (typeof onProgress === 'function') {
      onProgress(0, `Preparing documents (0/${documents.length})...`);
    }

    // Bulk-first strategy for signing: fetch temporary ZIP URL batches and extract PDFs once.
    // Missing files (if any) still fall back to per-document endpoint.
    const documentKeys = documents
      .map((doc) => normalizeDocumentKey(doc.original_document_id || doc.id))
      .filter(Boolean);

    if (documentKeys.length > 1) {
      const requestBatchSize = 10;
      try {
        for (let offset = 0; offset < documentKeys.length; offset += requestBatchSize) {
          const idChunk = documentKeys.slice(offset, offset + requestBatchSize);
          const idChunkSet = new Set(idChunk);
          const batchPayload = await getSigningDocumentFileBatchZipUrls(
            envelopeRecipientId,
            idChunk,
            signingToken,
            30
          );
          const batchItems = Array.isArray(batchPayload?.items) ? batchPayload.items : [];
          for (const batchItem of batchItems) {
            const expectedBatchKeys = Array.isArray(batchItem?.document_ids) && batchItem.document_ids.length > 0
              ? batchItem.document_ids
              : idChunk;
            const extractedBuffers = await extractPdfBuffersFromZipItem(batchItem, idChunkSet, expectedBatchKeys);
            extractedBuffers.forEach((buffer, docKey) => {
              if (!pdfBuffersByDocumentKey.has(String(docKey))) {
                pdfBuffersByDocumentKey.set(String(docKey), buffer);
              }
            });
          }
        }
      } catch (batchError) {
        console.warn('⚠️ Signing bulk document download failed, falling back to individual file API:', batchError);
      }
    }

    for (let docIndex = 0; docIndex < documents.length; docIndex++) {
      const doc = documents[docIndex];
      const docId = doc.original_document_id || doc.id;
      const docKey = normalizeDocumentKey(docId);

      try {
        let arrayBuffer = docKey ? pdfBuffersByDocumentKey.get(docKey) : null;
        if (!arrayBuffer) {
          arrayBuffer = await getSigningDocumentFile(envelopeRecipientId, docId, signingToken);
        }
        const pdf = await pdfjsLib.getDocument({
          data: arrayBuffer,
          verbosity: pdfjsLib.VerbosityLevel?.ERRORS,
        }).promise;
        const numPages = pdf.numPages;
        totalPageCount += numPages;

        for (let pageNum = 1; pageNum <= numPages; pageNum++) {
          const page = await pdf.getPage(pageNum);
          const viewport = page.getViewport({ scale: 1.0 });

          try {
            const svgString = await convertPageToSvg(page, viewport);
            allSvgPages.push({ documentIndex: docIndex, pageNumber: pageNum, svg: svgString, viewport });
            pageHeights.push(viewport.height);
          } catch {
            const ph = `<svg width="${viewport.width}" height="${viewport.height}" xmlns="http://www.w3.org/2000/svg"><rect width="100%" height="100%" fill="#f0f0f0"/><text x="50%" y="50%" text-anchor="middle" fill="#999">Error loading page</text></svg>`;
            allSvgPages.push({ documentIndex: docIndex, pageNumber: pageNum, svg: ph, viewport });
            pageHeights.push(viewport.height);
          }
        }
      } catch (err) {
        console.error(`Error loading PDF ${docIndex}:`, err);
      } finally {
        processedDocuments += 1;
        if (typeof onProgress === 'function') {
          const ratio = documents.length > 0 ? processedDocuments / documents.length : 1;
          onProgress(ratio, `Preparing documents (${processedDocuments}/${documents.length})...`);
        }
      }
    }

    // Merge into one SVG (same logic as CreateEnvelope)
    let cumulativeY = 0;
    const parts = [];
    const heightMap = [];
    const pageBreakH = 20;
    const maxW = Math.max(...allSvgPages.map((p) => p.viewport.width), 800);

    allSvgPages.forEach((pd, index) => {
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(pd.svg, 'image/svg+xml');
      const svgContent = svgDoc.documentElement.innerHTML;
      const w = pd.viewport.width;
      const h = pd.viewport.height;
      const offsetX = (maxW - w) / 2;

      if (index > 0) {
        parts.push(`<g transform="translate(0, ${cumulativeY})"><line x1="0" y1="0" x2="${maxW}" y2="0" stroke="#e0e0e0" stroke-width="2" stroke-dasharray="5,5"/><rect x="0" y="0" width="${maxW}" height="${pageBreakH}" fill="#f8f9fa" opacity="0.5"/></g>`);
        cumulativeY += pageBreakH;
      }

      parts.push(`<g transform="translate(${offsetX}, ${cumulativeY})"><rect x="0" y="0" width="${w}" height="${h}" fill="#ffffff" stroke="#d0d0d0" stroke-width="1"/>${svgContent}</g>`);

      heightMap.push({ y: cumulativeY, height: h, offsetX, width: w, documentIndex: pd.documentIndex, pageNumber: pd.pageNumber });
      cumulativeY += h;
    });

    const totalH = cumulativeY;
    const merged = `<svg width="${maxW}" height="${totalH}" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 ${maxW} ${totalH}">${parts.join('')}</svg>`;

    setMergedSvg(merged);
    setSvgPageHeights(heightMap);
    setSvgMaxWidth(maxW);
    setSvgTotalHeight(totalH);
    setTotalPages(totalPageCount);
  };

  // ─── Scroll tracking for current page ───────────────────────────────────
  const handleViewerScroll = useCallback(() => {
    if (!svgPageHeights.length) return;
    const viewer = svgContainerRef.current?.closest('.sign-document-viewer');
    if (!viewer) return;

    const scrollTop = viewer.scrollTop;
    const scale = getCurrentScale();

    for (let i = svgPageHeights.length - 1; i >= 0; i--) {
      const pageTop = svgPageHeights[i].y * scale;
      if (scrollTop >= pageTop - 50) {
        setCurrentPage(i + 1);
        return;
      }
    }
    setCurrentPage(1);
  }, [svgPageHeights, getCurrentScale]);

  useEffect(() => {
    const viewer = svgContainerRef.current?.closest('.sign-document-viewer');
    if (viewer) {
      viewer.addEventListener('scroll', handleViewerScroll);
      return () => viewer.removeEventListener('scroll', handleViewerScroll);
    }
  }, [handleViewerScroll]);

  // ─── Derived: strict ownership checks ─────────────────────────────────────
  const normalizedEnvelopeRecipientId = String(envelopeRecipientId || '');
  const normalizedRecipientUserId = String(signingData?.recipient?.id || '');
  const recipientName = String(signingData?.recipient?.name || '').trim();
  const recipientEmail = String(signingData?.recipient?.email || '').trim();

  const isNameFieldType = useCallback((fieldType) => {
    const normalized = String(fieldType || '').toLowerCase();
    return normalized === 'name';
  }, []);
  const isEmailFieldType = useCallback((fieldType) => {
    const normalized = String(fieldType || '').toLowerCase();
    return normalized === 'email';
  }, []);
  const isDateFieldType = useCallback((fieldType) => {
    return String(fieldType || '').toLowerCase() === 'date';
  }, []);

  const isFieldForCurrentRecipient = useCallback((field) => {
    if (!normalizedEnvelopeRecipientId) return false;

    const fieldEnvelopeRecipientId = field?.envelope_recipient_id;
    const fieldRecipientUserId = field?.recipient_id;

    // envelope_recipient_id is authoritative for this signing URL
    if (fieldEnvelopeRecipientId !== undefined && fieldEnvelopeRecipientId !== null && fieldEnvelopeRecipientId !== '') {
      if (String(fieldEnvelopeRecipientId) !== normalizedEnvelopeRecipientId) return false;
    } else {
      return false;
    }

    // If backend also includes recipient_id, it must match authenticated recipient user id
    if (
      normalizedRecipientUserId
      && fieldRecipientUserId !== undefined
      && fieldRecipientUserId !== null
      && fieldRecipientUserId !== ''
      && String(fieldRecipientUserId) !== normalizedRecipientUserId
    ) {
      return false;
    }

    return true;
  }, [normalizedEnvelopeRecipientId, normalizedRecipientUserId]);

  // ─── Derived: my fields vs read-only ────────────────────────────────────
  const myFields = useMemo(
    () => fields.filter((f) => (
      !isHiddenFieldType(f.field_type)
      && !f.filled_by_previous_signee
      && !f.read_only
      && isFieldForCurrentRecipient(f)
    )),
    [fields, isFieldForCurrentRecipient]
  );

  // Name/email fields share one value per type. Bootstrap once on load; refill on blur only (not while typing).
  useEffect(() => {
    const editableNameFields = myFields.filter((f) => isNameFieldType(f.field_type));
    if (editableNameFields.length === 0 || !recipientName) return;

    setFieldValues((prev) => {
      const firstFilled = editableNameFields
        .map((f) => prev[`field_${f.id}`])
        .find((v) => String(v || '').trim().length > 0);
      const canonicalName = firstFilled ?? recipientName;
      if (!String(canonicalName || '').trim()) return prev;

      let changed = false;
      const next = { ...prev };
      editableNameFields.forEach((f) => {
        const key = `field_${f.id}`;
        if ((next[key] ?? '') !== canonicalName) {
          next[key] = canonicalName;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [myFields, recipientName, isNameFieldType]);

  useEffect(() => {
    const editableEmailFields = myFields.filter((f) => isEmailFieldType(f.field_type));
    if (editableEmailFields.length === 0 || !recipientEmail) return;

    setFieldValues((prev) => {
      const firstFilled = editableEmailFields
        .map((f) => prev[`field_${f.id}`])
        .find((v) => String(v || '').trim().length > 0);
      const canonicalEmail = firstFilled ?? recipientEmail;
      if (!String(canonicalEmail || '').trim()) return prev;

      let changed = false;
      const next = { ...prev };
      editableEmailFields.forEach((f) => {
        const key = `field_${f.id}`;
        if ((next[key] ?? '') !== canonicalEmail) {
          next[key] = canonicalEmail;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [myFields, recipientEmail, isEmailFieldType]);

  const refillSharedRecipientFieldOnBlur = useCallback((field, rawValue) => {
    const isName = isNameFieldType(field.field_type);
    const isEmail = isEmailFieldType(field.field_type);
    if (!isName && !isEmail) return;
    if (String(rawValue ?? '').trim()) return;

    const source = isName ? recipientName : recipientEmail;
    if (!String(source || '').trim()) return;

    setFieldValues((prev) => {
      let changed = false;
      const next = { ...prev };
      myFields.forEach((f) => {
        const sameSharedType =
          (isName && isNameFieldType(f.field_type))
          || (isEmail && isEmailFieldType(f.field_type));
        if (!sameSharedType) return;
        const key = `field_${f.id}`;
        if ((next[key] ?? '') !== source) {
          next[key] = source;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [myFields, recipientName, recipientEmail, isNameFieldType, isEmailFieldType]);

  const requiredFields = useMemo(
    () => myFields.filter((f) => isSigningFieldRequired(f)),
    [myFields]
  );

  const isFieldValueValid = useCallback((field, rawValue) => {
    if (isCheckboxFieldType(field?.field_type)) {
      if (!isSigningFieldRequired(field)) return true;
      return isCheckboxFieldChecked(rawValue);
    }

    let value = String(rawValue ?? '').trim();
    if (!value) return false;
    if (isDateFieldType(field?.field_type)) {
      const ddmm = normalizeToDateDDMMYYYY(value);
      return isValidDateDDMMYYYY(ddmm);
    }
    if (isEmailFieldType(field?.field_type)) {
      if (signingFieldValueHasHtmlMarkup(value)) {
        value = value.replace(/<[^>]*>/g, '').trim();
      }
      return isValidEmailAddress(value);
    }
    return true;
  }, [isDateFieldType, isEmailFieldType]);

  const unfilledRequired = useMemo(
    () => requiredFields.filter((f) => !isFieldValueValid(f, fieldValues[`field_${f.id}`])),
    [requiredFields, fieldValues, isFieldValueValid]
  );

  // Only include current signee editable fields in submit payload.
  const submitFieldData = useMemo(() => {
    const allowedKeys = new Set(myFields.map((f) => `field_${f.id}`));
    const fieldByKey = new Map(myFields.map((f) => [`field_${f.id}`, f]));
    return Object.entries(fieldValues).reduce((acc, [key, value]) => {
      if (!allowedKeys.has(key)) return acc;
      const field = fieldByKey.get(key);
      if (field && isDateFieldType(field.field_type)) {
        acc[key] = normalizeToDateDDMMYYYY(value) || value;
      } else {
        acc[key] = value;
      }
      return acc;
    }, {});
  }, [myFields, fieldValues, isDateFieldType]);

  const allRequiredFilled = unfilledRequired.length === 0;
  const hasInvalidEmailFields = useMemo(
    () => myFields.some((f) => (
      isEmailFieldType(f.field_type)
      && String(fieldValues[`field_${f.id}`] || '').trim().length > 0
      && !isValidEmailAddress(String(fieldValues[`field_${f.id}`] || '').trim())
    )),
    [myFields, fieldValues, isEmailFieldType]
  );

  // ─── Start / Next field navigation (state only; logic below after scrollToField / openSignatureModal) ──
  const [currentFieldIndex, setCurrentFieldIndex] = useState(-1);

  useEffect(() => {
    if (!fieldNavError || currentFieldIndex < 0) return;
    const currentField = myFields[currentFieldIndex];
    if (!currentField) return;
    if (isFieldValueValid(currentField, fieldValues[`field_${currentField.id}`])) {
      setFieldNavError('');
    }
  }, [fieldNavError, currentFieldIndex, myFields, fieldValues, isFieldValueValid]);
  const signatureSourceByTypeRef = useRef({ signature: 'draw', initials: 'draw' });
  const signatureSubmitTypeRef = useRef('drawn');

  const mapSignatureSourceToSubmitType = useCallback((source) => {
    switch (String(source || '').toLowerCase()) {
      case 'adopt':
        return 'pre-selected style';
      case 'upload':
        return 'uploaded';
      case 'existing':
        return 'existing';
      case 'draw':
      default:
        return 'drawn';
    }
  }, []);

  // ─── Apply field value from modal ───────────────────────────────────────
  const applyFieldValue = (fieldId, value, signatureSource = '') => {
    setFieldValues((prev) => ({ ...prev, [`field_${fieldId}`]: value }));
    const targetField = fields.find((f) => String(f.id) === String(fieldId));
    if (targetField && isSignatureLikeField(targetField)) {
      const normalizedType = normalizeSignatureLikeType(targetField?.field_type);
      const previousSource = signatureSourceByTypeRef.current[normalizedType] || 'draw';
      const resolvedSource = signatureSource || previousSource;
      signatureSourceByTypeRef.current = {
        ...signatureSourceByTypeRef.current,
        [normalizedType]: resolvedSource,
      };
      signatureSubmitTypeRef.current = mapSignatureSourceToSubmitType(resolvedSource);
    }
    setActiveField(null);
  };

  // ─── Signature canvas drawing ───────────────────────────────────────────
  const getCanvasPointer = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const point = e.touches?.[0] || e.changedTouches?.[0] || e;
    const rawX = (point?.clientX ?? 0) - rect.left;
    const rawY = (point?.clientY ?? 0) - rect.top;
    // Map visual (CSS) coords to actual canvas resolution
    const scaleX = rect.width > 0 ? canvas.width / rect.width : 1;
    const scaleY = rect.height > 0 ? canvas.height / rect.height : 1;
    return { x: rawX * scaleX, y: rawY * scaleY };
  };

  const startDrawing = (e) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    if (e.cancelable) e.preventDefault();
    isDrawingRef.current = true;
    const { x, y } = getCanvasPointer(e, canvas);
    const stroke = [{ x, y }];
    activeStrokeRef.current = stroke;
    strokesRef.current.push(stroke);
  };

  const draw = (e) => {
    if (!isDrawingRef.current) return;
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    if (e.cancelable) e.preventDefault();
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasPointer(e, canvas);
    const activeStroke = activeStrokeRef.current;
    if (!activeStroke) return;
    activeStroke.push({ x, y });

    // Live preview while drawing
    const len = activeStroke.length;
    if (len < 2) return;
    const prev = activeStroke[len - 2];
    const curr = activeStroke[len - 1];
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#000000';
    ctx.beginPath();
    ctx.moveTo(prev.x, prev.y);
    ctx.lineTo(curr.x, curr.y);
    ctx.stroke();
  };

  const drawSmoothedStroke = (ctx, points) => {
    if (!points || points.length === 0) return;

    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#000000';
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);

    if (points.length === 1) {
      const p = points[0];
      ctx.lineTo(p.x + 0.01, p.y + 0.01);
    } else if (points.length === 2) {
      ctx.lineTo(points[1].x, points[1].y);
    } else {
      for (let i = 1; i < points.length - 1; i += 1) {
        const cx = (points[i].x + points[i + 1].x) / 2;
        const cy = (points[i].y + points[i + 1].y) / 2;
        ctx.quadraticCurveTo(points[i].x, points[i].y, cx, cy);
      }
      const last = points[points.length - 1];
      ctx.lineTo(last.x, last.y);
    }
    ctx.stroke();
  };

  const smoothSignatureCanvas = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    for (const stroke of strokesRef.current) {
      drawSmoothedStroke(ctx, stroke);
    }
  };

  const stopDrawing = () => {
    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;
    activeStrokeRef.current = null;
    // Smooth immediately when the user finishes the stroke.
    smoothSignatureCanvas();
  };

  const clearCanvas = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    strokesRef.current = [];
    activeStrokeRef.current = null;
  };

  const getCanvasDataUrl = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return null;
    const ctx = canvas.getContext('2d');
    const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    const hasContent = pixels.some((_, i) => i % 4 === 3 && pixels[i] > 0);
    if (!hasContent) return null;
    return canvas.toDataURL('image/png');
  };

  const getInitialsFromName = (rawValue) => {
    const raw = String(rawValue || '').trim();
    if (!raw) return '';
    const words = raw.split(/\s+/).filter(Boolean);
    if (words.length > 1) {
      return words.map((word) => word.charAt(0)).join('').toUpperCase().slice(0, 4);
    }
    const compact = raw.replace(/[^A-Za-z0-9]/g, '');
    if (!compact) return '';
    if (compact.length <= 4) return compact.toUpperCase();
    return compact.charAt(0).toUpperCase();
  };

  // ─── Generate adopt-style signatures from name ────────────────────────
  const generateAdoptStyles = async (name, useInitials = false) => {
    const displayText = useInitials ? getInitialsFromName(name) : String(name || '').trim();
    if (!displayText) { setAdoptStyles([]); return; }
    const fonts = [
      { label: 'Cursive', family: 'Dancing Script' },
      { label: 'Script', family: 'Great Vibes' },
      { label: 'Handwriting', family: 'Caveat' },
    ];

    // Ensure fonts are loaded before drawing on canvas
    try {
      await Promise.all(
        fonts.map(({ family }) =>
          document.fonts.load(`36px "${family}"`).catch(() => {})
        )
      );
    } catch {
      // Proceed even if font loading fails — fallback to browser default
    }

    const results = fonts.map(({ label, family }) => {
      const canvas = document.createElement('canvas');
      canvas.width = 420;
      canvas.height = 120;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, 420, 120);
      ctx.fillStyle = '#000000';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = `36px "${family}", cursive`;
      ctx.fillText(displayText, 210, 60);
      return { label, family, dataUrl: canvas.toDataURL('image/png') };
    });
    setAdoptStyles(results);
    setSelectedAdopt(null);
  };

  // ─── Handle uploaded signature image ──────────────────────────────────
  const handleSigUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setUploadedSigDataUrl(ev.target.result);
    reader.readAsDataURL(file);
  };

  const normalizeSignatureLikeType = useCallback((fieldType) => {
    const t = String(fieldType || '').toLowerCase();
    if (t === 'initial' || t === 'initials') return 'initials';
    if (t === 'signature') return 'signature';
    return '';
  }, []);

  const isSignatureLikeField = useCallback(
    (field) => {
      const normalized = normalizeSignatureLikeType(field?.field_type);
      return normalized === 'signature' || normalized === 'initials';
    },
    [normalizeSignatureLikeType]
  );

  const getReusableSignatureValue = useCallback((targetField) => {
    const normalizedTargetType = normalizeSignatureLikeType(targetField?.field_type);
    if (!targetField || !normalizedTargetType) {
      return '';
    }

    const candidate = myFields.find((f) => (
      normalizeSignatureLikeType(f.field_type) === normalizedTargetType &&
      !!fieldValues[`field_${f.id}`]
    ));

    return candidate ? (fieldValues[`field_${candidate.id}`] || '') : '';
  }, [myFields, fieldValues, normalizeSignatureLikeType]);

  // ─── Reset signature modal state when opening ─────────────────────────
  const openNewSignatureModal = (field) => {
    if (field.filled_by_previous_signee || field.read_only) return;
    if (!isFieldForCurrentRecipient(field)) return;
    const normalizedType = normalizeSignatureLikeType(field?.field_type);
    setSigTab('draw');
    setAdoptName(
      normalizedType === 'initials'
        ? getInitialsFromName(recipient?.name || '')
        : (recipient?.name || '')
    );
    setAdoptStyles([]);
    setSelectedAdopt(null);
    setUploadedSigDataUrl(null);
    setActiveField(field);
  };

  const openSignatureModal = (field) => {
    const reusable = getReusableSignatureValue(field);
    if (reusable) {
      setSignatureChoiceField(field);
      setSuggestedSignatureValue(reusable);
      return;
    }
    openNewSignatureModal(field);
  };

  const handleAdoptPreviousSignature = () => {
    if (!signatureChoiceField || !suggestedSignatureValue) return;
    const targetFieldId = signatureChoiceField.id;
    setSignatureChoiceField(null);
    setSuggestedSignatureValue('');
    applyFieldValue(targetFieldId, suggestedSignatureValue);
  };

  // ─── Submit signing ─────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!allRequiredFilled) {
      const firstInvalidRequiredIdx = myFields.findIndex(
        (f) => isSigningFieldRequired(f)
          && !isFieldValueValid(f, fieldValues[`field_${f.id}`])
      );
      if (firstInvalidRequiredIdx >= 0) {
        setFieldNavError('Required field');
        navigateToField(firstInvalidRequiredIdx);
        focusFieldElement(myFields[firstInvalidRequiredIdx]);
      }
      return;
    }
    if (hasInvalidEmailFields) {
      setError('Please enter a valid email address in all email fields before submitting.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const payload = {
        field_data: submitFieldData,
        signature_type: signatureSubmitTypeRef.current || 'drawn',
        ip: signeeIp,
      };

      const result = await submitSigning(envelopeRecipientId, signingToken, payload);
      if (isAlreadySignedPayload(result)) {
        showAlreadySignedScreen(result);
        return;
      }
      if (result?.success === false) {
        setError(result?.message || 'Failed to submit signing.');
        return;
      }
      const data = extractSigningPayloadData(result) || result;
      setCompletionData(data);
      setCompleted(true);
    } catch (err) {
      const errorPayload = err?.response?.data;
      const msg = errorPayload?.message || err?.message || 'Failed to submit signing.';
      if (isAlreadySignedPayload(errorPayload) || isAlreadySignedPayload(msg)) {
        showAlreadySignedScreen(errorPayload || { message: msg });
        return;
      }
      if (err?.response?.status === 401) {
        redirectToOtpWithError(msg);
        return;
      }
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Decline signing ───────────────────────────────────────────────────
  const handleDecline = async () => {
    if (declining) return;
    setDeclining(true);

    try {
      const result = await declineSigning(envelopeRecipientId, signingToken, declineReason);
      const data = result?.data || result;
      setShowDecline(false);
      setDeclineData({ ...data, reason: declineReason });
      setDeclined(true);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to decline.';
      if (err?.response?.status === 401) {
        redirectToOtpWithError(msg);
        return;
      }
      setError(msg);
    } finally {
      setDeclining(false);
    }
  };

  // Scroll to a field
  const scrollToField = (field) => {
    const fieldEl = document.querySelector(`[data-field-id="${field?.id}"]`);
    const isMobileLayout = window.matchMedia('(max-width: 768px)').matches;

    if (isMobileLayout) {
      if (fieldEl?.scrollIntoView) {
        fieldEl.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      }
      return;
    }

    const viewer = svgContainerRef.current?.closest('.sign-document-viewer');
    if (viewer && svgPageHeights.length) {
      const scale = getCurrentScale();
      const pageInfo = resolveFieldPageInfo(field);
      if (pageInfo) {
        const fieldTop = (pageInfo.y + (field.y_position || 0)) * scale;
        viewer.scrollTo({ top: fieldTop - 100, behavior: 'smooth' });
        // Fallback alignment via DOM element to ensure exact in-view positioning.
        if (fieldEl?.scrollIntoView) {
          window.setTimeout(() => {
            fieldEl.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
          }, 60);
        }
        return;
      }
    }
    if (fieldEl?.scrollIntoView) {
      fieldEl.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    }
  };

  // ─── Start / Next field navigation (logic) ────────────────────────────
  const focusFieldElement = (field) => {
    const el = document.querySelector(`[data-field-id="${field.id}"]`);
    if (!el) return;

    if (isSignatureLikeField(field)) {
      openSignatureModal(field);
    } else if (field.field_type === 'checkbox') {
      el.click();
    } else if (isDateFieldType(field.field_type)) {
      const input = el.querySelector('input[type="date"]');
      if (input) {
        input.focus();
        if (typeof input.showPicker === 'function') {
          try {
            input.showPicker();
          } catch {
            // showPicker may require a direct user gesture
          }
        }
      }
    } else {
      const input = el.querySelector('input');
      if (input) {
        input.focus();
        input.select();
      }
    }
  };

  const navigateToField = (index) => {
    if (index < 0 || index >= myFields.length) return;
    const field = myFields[index];
    setFieldNavError('');
    setCurrentFieldIndex(index);
    scrollToField(field);
    // Focus immediately (for gesture-chain on mobile) and again after scroll settles
    focusFieldElement(field);
    setTimeout(() => focusFieldElement(field), 450);
  };

  const scrollToSubmitButton = () => {
    if (!completeBtnRef.current) return;
    const isMobileLayout = window.matchMedia('(max-width: 768px)').matches;
    if (isMobileLayout) {
      completeBtnRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.setTimeout(() => {
        completeBtnRef.current?.focus();
      }, 300);
      return;
    }
    completeBtnRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const rect = completeBtnRef.current.getBoundingClientRect();
    const absoluteTop = window.scrollY + rect.top - 120;
    window.scrollTo({ top: Math.max(0, absoluteTop), behavior: 'smooth' });
    setTimeout(() => {
      completeBtnRef.current?.focus();
    }, 300);
  };

  const handleStartNext = () => {
    if (currentFieldIndex < 0) {
      setFieldNavError('');
      const firstUnfilledIdx = myFields.findIndex(
        (f) => !isFieldValueValid(f, fieldValues[`field_${f.id}`])
      );
      navigateToField(firstUnfilledIdx >= 0 ? firstUnfilledIdx : 0);
      return;
    }

    if (allRequiredFilled) {
      setFieldNavError('');
      scrollToSubmitButton();
      return;
    }

    const currentField = myFields[currentFieldIndex];
    if (
      isSigningFieldRequired(currentField)
      && !isFieldValueValid(currentField, fieldValues[`field_${currentField.id}`])
    ) {
      setFieldNavError('Required field');
      scrollToField(currentField);
      focusFieldElement(currentField);
      return;
    }

    setFieldNavError('');

    let nextIdx = -1;
    for (let i = currentFieldIndex + 1; i < myFields.length; i++) {
      if (!isFieldValueValid(myFields[i], fieldValues[`field_${myFields[i].id}`])) {
        nextIdx = i;
        break;
      }
    }
    if (nextIdx < 0) {
      for (let i = 0; i < currentFieldIndex; i++) {
        if (!isFieldValueValid(myFields[i], fieldValues[`field_${myFields[i].id}`])) {
          nextIdx = i;
          break;
        }
      }
    }
    if (nextIdx < 0) {
      setFieldNavError('');
      scrollToSubmitButton();
      return;
    }
    navigateToField(nextIdx);
  };

  const isStarted = currentFieldIndex >= 0;

  // ─── Render: loading ───────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="sign-loading">
        <Spinner animation="border" variant="primary" />
        <p>{loadingStatus}</p>
        <div
          style={{
            width: 'min(420px, 88vw)',
            marginTop: '10px',
          }}
        >
          <div
            style={{
              height: '10px',
              background: '#e9ecef',
              borderRadius: '999px',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${Math.max(0, Math.min(100, loadingProgress))}%`,
                height: '100%',
                background: '#0d6efd',
                transition: 'width 200ms ease',
              }}
            />
          </div>
          <p className="text-muted small mt-2 mb-0">{Math.max(0, Math.min(100, loadingProgress))}%</p>
        </div>
      </div>
    );
  }

  // ─── Render: already signed (API: success false or already_signed flag) ──
  if (alreadySigned) {
    const signedAt = alreadySignedData?.signed_at
      ? formatDateTimeUTC(alreadySignedData.signed_at)
      : null;
    const envelopeName =
      alreadySignedData?.subject
      || signingData?.envelope?.subject
      || signingData?.subject
      || 'Document';

    return (
      <div className="status-screen status-screen--signed">
        <div className="status-card">
          <div
            className="status-icon-circle status-icon--info"
          >
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <circle cx="24" cy="24" r="24" fill="#dbeafe" />
              <circle cx="24" cy="24" r="16" stroke="#3b82f6" strokeWidth="2.5" fill="none" />
              <circle cx="24" cy="18" r="2" fill="#3b82f6" />
              <rect x="22.5" y="22" width="3" height="10" rx="1.5" fill="#3b82f6" />
            </svg>
          </div>
          <h1 className="status-title">Already Signed</h1>
          <p className="status-subtitle">
            {alreadySignedData?.message
              || 'You have already signed this document. No further action is required. A completed copy will be sent to you once all parties have signed.'}
          </p>
          <div className="status-details">
            <div className="status-detail-row">
              <span className="status-label">Status</span>
              <span className="status-value status-val--signed">&#10003; Previously Signed</span>
            </div>
            {signedAt && (
              <div className="status-detail-row">
                <span className="status-label">Signed At</span>
                <span className="status-value">{signedAt}</span>
              </div>
            )}
            <div className="status-detail-row">
              <span className="status-label">Subject</span>
              <span className="status-value">{envelopeName}</span>
            </div>
          </div>
          <p className="status-footer">Powered by <strong>VolkSign</strong> &mdash; Secure Digital Signatures</p>
        </div>
      </div>
    );
  }

  // ─── Render: completion screen ─────────────────────────────────────────
  if (completed) {
    const allDone = completionData?.all_signees_completed;
    const signedAt = completionData?.signed_at
      ? formatDateTimeUTC(completionData.signed_at)
      : formatDateTimeUTC(new Date());
    const envelopeName = signingData?.envelope?.subject || 'Document';
    const envelopeStatus = allDone ? 'Completed' : 'In Progress';

    return (
      <div className="sign-complete-screen">
        <div className="complete-card">
          {/* Check icon */}
          <div className="complete-check-circle">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <circle cx="24" cy="24" r="24" fill="#dcfce7" />
              <path d="M15 24.5L21 30.5L33 18.5" stroke="#16a34a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>

          <h1>Document Signed Successfully</h1>
          <p className="complete-subtitle">
            Thank you! Your signature has been recorded. A signed copy of the document and a completion certificate will be emailed to all recipients once all parties have signed.
          </p>

          {/* Details table */}
          <div className="complete-details">
            <div className="complete-detail-row">
              <span className="detail-label">Your Status</span>
              <span className="detail-value status-signed">&#10003; Signed</span>
            </div>
            <div className="complete-detail-row">
              <span className="detail-label">Signed At</span>
              <span className="detail-value">{signedAt}</span>
            </div>
            <div className="complete-detail-row">
              <span className="detail-label">Envelope</span>
              <span className="detail-value">{envelopeName}</span>
            </div>
            <div className="complete-detail-row">
              <span className="detail-label">Envelope Status</span>
              <span className={`detail-value status-${allDone ? 'completed' : 'progress'}`}>{envelopeStatus}</span>
            </div>
          </div>

          <p className="complete-footer">Powered by <strong>VolkSign</strong> &mdash; Secure Digital Signatures</p>
        </div>
      </div>
    );
  }

  // ─── Render: decline screen ──────────────────────────────────────────
  if (declined) {
    const declinedAt = declineData?.declined_at
      ? formatDateTimeUTC(declineData.declined_at)
      : formatDateTimeUTC(new Date());
    const envelopeName = signingData?.envelope?.subject || 'Document';

    return (
      <div className="sign-declined-screen">
        <div className="declined-card">
          {/* X icon */}
          <div className="declined-x-circle">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <circle cx="24" cy="24" r="24" fill="#fee2e2" />
              <path d="M17 17L31 31M31 17L17 31" stroke="#dc2626" strokeWidth="3" strokeLinecap="round" />
            </svg>
          </div>

          <h1>Document Declined</h1>
          <p className="declined-subtitle">
            You have declined to sign this document. The sender has been notified of your decision.
          </p>

          {/* Reason box (only if a reason was provided) */}
          {declineData?.reason && (
            <div className="declined-reason-box">
              <span className="declined-reason-label">REASON</span>
              <p className="declined-reason-text">{declineData.reason}</p>
            </div>
          )}

          {/* Details table */}
          <div className="complete-details">
            <div className="complete-detail-row">
              <span className="detail-label">Status</span>
              <span className="detail-value status-declined">&#10007; Declined</span>
            </div>
            <div className="complete-detail-row">
              <span className="detail-label">Date</span>
              <span className="detail-value">{declinedAt}</span>
            </div>
            <div className="complete-detail-row">
              <span className="detail-label">Envelope</span>
              <span className="detail-value">{envelopeName}</span>
            </div>
          </div>

          <p className="complete-footer">Powered by <strong>VolkSign</strong> &mdash; Secure Digital Signatures</p>
        </div>
      </div>
    );
  }

  // ─── Render: error (no data) ───────────────────────────────────────────
  if (!signingData && error) {
    return (
      <div className="sign-loading">
        <p className="text-danger">{error}</p>
      </div>
    );
  }

  // ─── Compute field positions for rendering ─────────────────────────────
  const scale = getCurrentScale();
  const recipient = signingData?.recipient;
  const docTitle =
    signingData?.envelope?.subject ||
    signingData?.subject ||
    signingData?.envelope_subject ||
    'Document';

  return (
    <div
      className={`sign-document-page${zoomLevel === 'fitToWidth' ? ' is-fit-width' : ' is-zoomed'}`}
      ref={pageContainerRef}
    >
      {/* ── Header ──────────────────────────────────────── */}
      <div className="sign-header">
        <div className="sign-header-left">
          <img src={logo} alt="VolkSign" className="header-logo" />
        </div>
        <div className="sign-header-center">
          <p className="doc-title">{docTitle}</p>
        </div>
        <div className="sign-header-right" />
      </div>

      {/* ── Toolbar ─────────────────────────────────────── */}
      <div className="sign-toolbar">
        <span className="toolbar-label">Document Preview</span>
        <select className="zoom-select-signing" value={zoomLevel} onChange={(e) => setZoomLevel(e.target.value)}>
          <option value="fitToWidth">Fit to Width</option>
          <option value="50">50%</option>
          <option value="75">75%</option>
          <option value="100">100%</option>
          <option value="125">125%</option>
          <option value="150">150%</option>
          <option value="200">200%</option>
          <option value="250">250%</option>
          <option value="300">300%</option>
        </select>
        <button className="toolbar-btn" onClick={() => {
          if (!signingData?.documents?.[0]) return;
          const docId = signingData.documents[0].original_document_id || signingData.documents[0].id;
          getSigningDocumentFile(envelopeRecipientId, docId, signingToken).then((ab) => {
            const blob = new Blob([ab], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = docTitle;
            a.click();
            URL.revokeObjectURL(url);
          });
        }}>
          Download
        </button>
      </div>

      {/* ── Mobile nav bar (shown on mobile only) ────────── */}
      {myFields.length > 0 && (
        <div className="sign-mobile-nav">
          <button
            className={`sign-nav-btn${allRequiredFilled ? ' all-done' : ''}`}
            onClick={handleStartNext}
          >
            {!isStarted ? 'Start' : allRequiredFilled ? 'All Fields Completed' : 'Next'}
          </button>
          {fieldNavError ? (
            <span className="sign-field-nav-error" role="alert">{fieldNavError}</span>
          ) : null}
          <span className="sign-mobile-progress">
            {myFields.filter((f) => !!fieldValues[`field_${f.id}`]).length}/{myFields.length} fields
          </span>
        </div>
      )}

      {/* ── Body: viewer + sidebar ──────────────────────── */}
      <div className="sign-body">
        {/* Document viewer */}
        <div className="sign-document-viewer">
          <div className="sign-svg-wrapper">
          <div
            className="sign-svg-container"
            ref={svgContainerRef}
            style={{
              width: svgMaxWidth > 0 ? `${svgMaxWidth * scale}px` : 'auto',
              height: svgTotalHeight > 0 ? `${svgTotalHeight * scale}px` : 'auto',
            }}
          >
            {/* SVG layer — no pointer events */}
            {mergedSvg && (
              <div
                dangerouslySetInnerHTML={{ __html: mergedSvg }}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  transform: `scale(${scale})`,
                  transformOrigin: 'top left',
                  width: svgMaxWidth,
                  height: svgTotalHeight,
                  pointerEvents: 'none',
                  zIndex: 1,
                }}
              />
            )}

            {/* Fields overlay layer — sits above SVG, receives all interactions */}
            <div
              className="signing-fields-layer"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 2,
                pointerEvents: 'none',
              }}
            >
              {fields.map((field) => {
                if (isHiddenFieldType(field.field_type)) return null;
                const belongsToMe = isFieldForCurrentRecipient(field);
                // Signing view: hide all fields that belong to other recipients.
                if (!belongsToMe) return null;

                const pageInfo = resolveFieldPageInfo(field);
                if (!pageInfo) return null;

                const fieldX = (pageInfo.offsetX || 0) + (field.x_position || 0);
                const fieldY = pageInfo.y + (field.y_position || 0);
                // Some signing payloads provide width/height under different keys.
                // Also avoid `||` so valid `0` values don't get replaced.
                const rawFieldW = field.width ?? field.field_width ?? field.w ?? null;
                const rawFieldH = field.height ?? field.field_height ?? field.h ?? null;
                const parsedW = rawFieldW != null ? parseFloat(rawFieldW) : NaN;
                const parsedH = rawFieldH != null ? parseFloat(rawFieldH) : NaN;
                // If backend doesn't provide sizes, use half of previous defaults.
                const fieldW = Number.isFinite(parsedW) ? parsedW : 75;
                const fieldH = Number.isFinite(parsedH) ? parsedH : 20;

                const isMine = belongsToMe && !field.filled_by_previous_signee && !field.read_only;
                const isReadOnly = field.filled_by_previous_signee || field.read_only || !isMine;
                const isDateSigned = field.field_type === 'dateSigned';
                const rawValue = fieldValues[`field_${field.id}`] || field.field_value || field.value || field.default_value || '';
                const value = rawValue;
                // Decide rich vs plain from the source content (not the live edit) so the
                // editor never swaps to a plain input mid-edit when the text is cleared.
                const fieldSourceValue = field.field_value || field.value || field.default_value || '';
                const fieldIsRichText =
                  signingFieldValueHasHtmlMarkup(fieldSourceValue)
                  && !isEmailFieldType(field.field_type)
                  && !isDateFieldType(field.field_type);
                const dateInputValue = isDateFieldType(field.field_type)
                  ? (normalizeToDateISO(rawValue) || '')
                  : '';
                const dateDisplayValue = isDateFieldType(field.field_type)
                  ? (normalizeToDateDDMMYYYY(rawValue) || '')
                  : '';
                const readOnlyDateDisplay = isDateFieldType(field.field_type)
                  ? (normalizeToDateDDMMYYYY(rawValue) || String(rawValue || '').trim())
                  : rawValue;
                const isEmailField = isEmailFieldType(field.field_type);
                const emailInvalid = isEmailField
                  && String(value || '').trim().length > 0
                  && !isValidEmailAddress(String(value || '').trim());
                const isFilled = isFieldValueValid(field, rawValue);
                const color = recipient?.signee_color || '#6366f1';
                const isNavigatedField = currentFieldIndex >= 0 && myFields[currentFieldIndex]?.id === field.id;
                const showRequiredNavError = Boolean(
                  fieldNavError && isNavigatedField && isSigningFieldRequired(field) && !isFilled
                );
                const requiredErrorClass = showRequiredNavError ? ' required-error' : '';
                // Use the field's own font size (default 12px, matching envelope creation)
                // scaled to the rendered PDF, instead of a fixed heuristic.
                const fieldDefinedFontPx = parseInt(String(field.font_size ?? field.fontSize ?? 12), 10);
                const baseFieldFontPx = Math.max(2, Number.isFinite(fieldDefinedFontPx) ? fieldDefinedFontPx : 12);
                const scaledFontSize = baseFieldFontPx * scale;

                const commonStyle = {
                  position: 'absolute',
                  left: `${fieldX * scale}px`,
                  top: `${fieldY * scale}px`,
                  width: `${fieldW * scale}px`,
                  height: `${fieldH * scale}px`,
                  borderColor: isReadOnly
                    ? 'transparent'
                    : (showRequiredNavError || emailInvalid ? '#dc3545' : color),
                  color: isReadOnly ? '#1f2937' : color,
                  backgroundColor: isReadOnly ? 'transparent' : (isFilled ? 'rgba(255,255,255,0.92)' : `${color}18`),
                  fontSize: `${scaledFontSize}px`,
                  pointerEvents: isReadOnly ? 'none' : 'auto',
                };

                // Editable text-like fields use the same transform-scale model as the
                // read-only fields: the box is sized in base PDF units and scaled via
                // transform, so the font keeps its configured size (e.g. 12px) instead of
                // being multiplied by the page scale.
                const editableScaledBoxStyle = {
                  position: 'absolute',
                  left: `${fieldX * scale}px`,
                  top: `${fieldY * scale}px`,
                  width: `${fieldW}px`,
                  height: `${fieldH}px`,
                  transform: `scale(${scale})`,
                  transformOrigin: 'top left',
                  borderColor: (showRequiredNavError || emailInvalid) ? '#dc3545' : color,
                  color,
                  backgroundColor: isFilled ? 'rgba(255,255,255,0.92)' : `${color}18`,
                  pointerEvents: 'auto',
                };

                // ── Signature / Initials: click to open drawing modal ──
                if (isSignatureLikeField(field)) {
                  return (
                    <div
                      key={field.id}
                      data-field-id={field.id}
                      className={`signing-field signing-field-sig${isFilled ? ' filled' : ''}${isReadOnly ? ' read-only' : ''}${activeField?.id === field.id ? ' active' : ''}${isNavigatedField ? ' navigated' : ''}${requiredErrorClass}`}
                      style={commonStyle}
                      onClick={() => !isReadOnly && openSignatureModal(field)}
                      title={field.field_name || field.field_type}
                    >
                      {value ? (
                        <img
                          src={value}
                          alt={normalizeSignatureLikeType(field.field_type) === 'initials' ? 'initials' : 'signature'}
                          className="field-value-img"
                        />
                      ) : (
                        <span className="field-placeholder">{field.field_name || field.field_type}{field.is_required ? ' *' : ''}</span>
                      )}
                    </div>
                  );
                }

                // ── date_signed: auto-filled with current date, read-only ──
                if (isDateSigned) {
                  return (
                    <div
                      key={field.id}
                      data-field-id={field.id}
                      className="signing-field signing-field-date filled read-only"
                      style={{ ...commonStyle, cursor: 'default' }}
                      title={field.field_name || 'Date Signed'}
                    >
                      <span className="field-date-value">{value || ''}</span>
                    </div>
                  );
                }

                // ── Checkbox: inline toggle ──
                if (field.field_type === 'checkbox') {
                  const isChecked = isCheckboxFieldChecked(value);
                  const optionLabel = (() => {
                    if (Array.isArray(field.options) && field.options[0] != null) {
                      return String(field.options[0]);
                    }
                    if (field.field_options != null && String(field.field_options).trim() !== '') {
                      return String(field.field_options);
                    }
                    if (field.option_label != null) return String(field.option_label);
                    return '';
                  })();
                  return (
                    <div
                      key={field.id}
                      data-field-id={field.id}
                      className={`signing-field signing-field-checkbox${isChecked ? ' filled' : ''}${isReadOnly ? ' read-only' : ''}${isNavigatedField ? ' navigated' : ''}${requiredErrorClass}`}
                      style={{ ...commonStyle, cursor: isReadOnly ? 'default' : 'pointer' }}
                      onClick={() => {
                        if (isReadOnly) return;
                        setFieldValues((prev) => ({
                          ...prev,
                          [`field_${field.id}`]: isChecked ? 'false' : 'true',
                        }));
                      }}
                      title={field.field_name || 'Checkbox'}
                    >
                      <span className="field-checkbox-icon" style={{ fontSize: `${Math.max(14, 18 * scale)}px` }}>
                        {isChecked ? '\u2611' : '\u2610'}
                      </span>
                      {optionLabel ? (
                        <span
                          className="field-checkbox-label"
                          style={{
                            fontSize: `${scaledFontSize}px`,
                            marginLeft: `${4 * scale}px`,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {optionLabel}
                        </span>
                      ) : null}
                    </div>
                  );
                }

                // ── Text / Name / other fields: inline editable input ──
                if (isReadOnly) {
                  const richHtml = signingFieldValueHasHtmlMarkup(value);
                  const presentation = resolveReadOnlyFieldPresentation(field, value);
                  const alignStyle = readOnlyPresentationToFlexStyle(presentation.textAlign);
                  const valueStyle = {
                    ...readOnlyPresentationToInlineStyle(presentation),
                    minHeight: '100%',
                  };
                  const readOnlyFieldStyle = {
                    position: 'absolute',
                    left: `${fieldX * scale}px`,
                    top: `${fieldY * scale}px`,
                    width: `${fieldW}px`,
                    height: `${fieldH}px`,
                    transform: `scale(${scale})`,
                    transformOrigin: 'top left',
                    border: 'none',
                    backgroundColor: 'transparent',
                    pointerEvents: 'none',
                    color: '#1f2937',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    ...(richHtml
                      ? { justifyContent: 'flex-start', alignItems: 'flex-start' }
                      : alignStyle),
                  };
                  const readOnlyAlignClass = richHtml
                    ? ''
                    : ` signing-field-text--align-${presentation.textAlign}`;
                  return (
                    <div
                      key={field.id}
                      data-field-id={field.id}
                      className={`signing-field signing-field-text filled read-only${readOnlyAlignClass}`}
                      style={readOnlyFieldStyle}
                      title={field.field_name || field.field_type}
                    >
                      {richHtml ? (
                        <div
                          className="field-readonly-value-display field-readonly-value field-readonly-value--html"
                          style={valueStyle}
                          dangerouslySetInnerHTML={{
                            __html: prepareReadOnlyValueHtml(
                              unwrapReadOnlyFieldValueWrapper(String(value || ''))
                            ),
                          }}
                        />
                      ) : (
                        <div className="field-readonly-value-display field-readonly-value" style={valueStyle}>
                          {isDateFieldType(field.field_type) ? readOnlyDateDisplay : (value || '')}
                        </div>
                      )}
                    </div>
                  );
                }

                if (isDateFieldType(field.field_type)) {
                  const openDatePicker = (inputEl) => {
                    if (!inputEl) return;
                    inputEl.focus();
                    if (typeof inputEl.showPicker === 'function') {
                      try {
                        inputEl.showPicker();
                      } catch {
                        // showPicker may be blocked outside a trusted gesture
                      }
                    }
                  };
                  return (
                    <div
                      key={field.id}
                      data-field-id={field.id}
                      className={`signing-field signing-field-date signing-field-simple-date${isFilled ? ' filled' : ''}${isNavigatedField ? ' navigated' : ''}${requiredErrorClass}`}
                      style={{ ...editableScaledBoxStyle, padding: '0 4px', cursor: 'pointer', position: 'relative' }}
                      title={field.field_name || field.field_type}
                      onClick={(e) => {
                        e.stopPropagation();
                        openDatePicker(e.currentTarget.querySelector('input[type="date"]'));
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <span
                        className={`field-date-value${dateDisplayValue ? '' : ' field-date-value--placeholder'}`}
                        style={{ fontSize: `${baseFieldFontPx}px`, color: '#1f2937' }}
                      >
                        {dateDisplayValue || 'dd/mm/yyyy'}
                      </span>
                      <input
                        type="date"
                        className="signing-inline-input signing-inline-input--date"
                        aria-label={field.field_name || 'Date'}
                        value={dateInputValue}
                        onClick={(e) => {
                          e.stopPropagation();
                          openDatePicker(e.currentTarget);
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          const isoValue = e.target.value;
                          const storedValue = isoValue
                            ? (normalizeToDateDDMMYYYY(isoValue) || isoValue)
                            : '';
                          setFieldValues((prev) => ({
                            ...prev,
                            [`field_${field.id}`]: storedValue,
                          }));
                        }}
                      />
                    </div>
                  );
                }

                if (fieldIsRichText) {
                  const presentation = resolveReadOnlyFieldPresentation(field, value);
                  const richEditableStyle = {
                    ...readOnlyPresentationToInlineStyle(presentation),
                    outline: 'none',
                    cursor: 'text',
                  };
                  const richInitialHtml = prepareReadOnlyValueHtml(
                    unwrapReadOnlyFieldValueWrapper(String(value || ''))
                  );
                  const persistRichValue = (rawHtml) => {
                    const inner = prepareReadOnlyValueHtml(
                      unwrapReadOnlyFieldValueWrapper(String(rawHtml || ''))
                    );
                    const wrapped = inner
                      ? wrapReadOnlyFieldTypographyHtml(inner, {
                          fontFamily: presentation.fontFamily,
                          fontSize: presentation.fontSize,
                          fontColor: presentation.fontColor,
                          textAlign: presentation.textAlign,
                        })
                      : '';
                    setFieldValues((prev) => ({
                      ...prev,
                      [`field_${field.id}`]: wrapped,
                    }));
                  };
                  return (
                    <div
                      key={field.id}
                      data-field-id={field.id}
                      className={`signing-field signing-field-text signing-field-text--rich${isFilled ? ' filled' : ''}${isNavigatedField ? ' navigated' : ''}${requiredErrorClass}`}
                      style={{ ...editableScaledBoxStyle, padding: 0, cursor: 'text' }}
                      title={field.field_name || field.field_type}
                    >
                      <SigningInlineRichField
                        className="signing-inline-input signing-inline-input--rich"
                        style={richEditableStyle}
                        initialHtml={richInitialHtml}
                        onChangeHtml={persistRichValue}
                      />
                    </div>
                  );
                }

                return (
                  <div
                    key={field.id}
                    data-field-id={field.id}
                    className={`signing-field signing-field-text${isFilled ? ' filled' : ''}${isNavigatedField ? ' navigated' : ''}${requiredErrorClass}`}
                    style={{ ...editableScaledBoxStyle, padding: 0, cursor: 'text' }}
                    title={field.field_name || field.field_type}
                  >
                    <input
                      type={isEmailField ? 'email' : 'text'}
                      className="signing-inline-input"
                      style={{
                        fontSize: `${baseFieldFontPx}px`,
                        color: '#1f2937',
                        borderColor: emailInvalid ? '#dc3545' : undefined,
                      }}
                      placeholder={field.field_name || field.field_type || 'Type here...'}
                      value={value || ''}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        const nextValue = e.target.value;
                        setFieldValues((prev) => {
                          if (isNameFieldType(field.field_type) || isEmailFieldType(field.field_type)) {
                            const next = { ...prev };
                            myFields.forEach((f) => {
                              const sameSharedType =
                                (isNameFieldType(field.field_type) && isNameFieldType(f.field_type))
                                || (isEmailFieldType(field.field_type) && isEmailFieldType(f.field_type));
                              if (sameSharedType) {
                                next[`field_${f.id}`] = nextValue;
                              }
                            });
                            return next;
                          }
                          return {
                            ...prev,
                            [`field_${field.id}`]: nextValue,
                          };
                        });
                      }}
                      onBlur={(e) => refillSharedRecipientFieldOnBlur(field, e.target.value)}
                    />
                  </div>
                );
              })}
            </div>
          </div>
          </div>
        </div>

        {/* ── Right sidebar ─────────────────────────────── */}
        <div className="sign-sidebar">
          <div className="sign-sidebar-header">
            <h2>Complete Document</h2>
          </div>

          <div className="sign-sidebar-body">
            <div className="sign-instructions">
              <span className="instruction-icon">&#128196;</span>
              <span>Please review and complete all required fields on the document</span>
            </div>

            {error && (
              <div className="otp-error-banner" style={{ margin: 0 }}>
                <span>{error}</span>
              </div>
            )}

            {/* Start / Next button */}
            {myFields.length > 0 && (
              <div className="sign-nav-buttons">
                <button
                  className={`sign-nav-btn${allRequiredFilled ? ' all-done' : ''}`}
                  onClick={handleStartNext}
                >
                  {!isStarted ? 'Start' : allRequiredFilled ? 'All Fields Completed' : 'Next'}
                </button>
                {fieldNavError ? (
                  <p className="sign-field-nav-error" role="alert">{fieldNavError}</p>
                ) : null}
              </div>
            )}

            {/* Fields toggle — list opens as overlay panel */}
            {myFields.length > 0 && (
              <button
                type="button"
                className={`sign-fields-toggle${fieldsPanelOpen ? ' open' : ''}`}
                onClick={() => setFieldsPanelOpen((prev) => !prev)}
                aria-expanded={fieldsPanelOpen}
                aria-controls="sign-fields-panel"
              >
                <span className="sign-fields-toggle-label">
                  <i className="ri-list-check-2" aria-hidden="true" />
                  Fields
                </span>
                <span className="sign-fields-toggle-meta">
                  {myFields.filter((f) => isFieldValueValid(f, fieldValues[`field_${f.id}`])).length}/{myFields.length}
                  <i className={`ri-arrow-${fieldsPanelOpen ? 'down' : 'up'}-s-line`} aria-hidden="true" />
                </span>
              </button>
            )}
          </div>

          {/* Collapsible fields panel — overlaps sidebar when open */}
          {myFields.length > 0 && (
            <div
              className={`sign-fields-panel-backdrop${fieldsPanelOpen ? ' open' : ''}`}
              onClick={() => setFieldsPanelOpen(false)}
              aria-hidden={!fieldsPanelOpen}
            />
          )}
          {myFields.length > 0 && (
            <div
              id="sign-fields-panel"
              className={`sign-fields-panel${fieldsPanelOpen ? ' open' : ''}`}
              role="dialog"
              aria-label="Document fields"
              aria-hidden={!fieldsPanelOpen}
            >
              <div className="sign-fields-panel-header">
                <h3>Fields</h3>
                <button
                  type="button"
                  className="sign-fields-panel-close"
                  onClick={() => setFieldsPanelOpen(false)}
                  aria-label="Close fields panel"
                >
                  <i className="ri-close-line" aria-hidden="true" />
                </button>
              </div>
              <div className="sign-field-list">
                {myFields.map((field, idx) => {
                  const value = fieldValues[`field_${field.id}`];
                  const isFilled = isFieldValueValid(field, value);
                  const isActive = idx === currentFieldIndex;
                  return (
                    <div
                      key={field.id}
                      className={`sign-field-item${isFilled ? ' completed' : ''}${field.is_required ? ' required' : ''}${isActive ? ' active' : ''}`}
                      onClick={() => {
                        navigateToField(idx);
                        setFieldsPanelOpen(false);
                      }}
                    >
                      <span className="field-item-label">{field.field_name || field.field_type}</span>
                      <span className="field-page-badge">Page {field.page_number || 1}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="sign-sidebar-actions">
            <button className="decline-btn" onClick={() => setShowDecline(true)} disabled={submitting}>
              &#10060; Decline to Sign
            </button>
            <button ref={completeBtnRef} className="complete-btn" onClick={handleSubmit} disabled={submitting || !allRequiredFilled || hasInvalidEmailFields}>
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" />
                  Submitting...
                </>
              ) : (
                <>Submit Signature</>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* ── Signature / Initials modal (3 tabs) ─────────── */}
      {activeField && isSignatureLikeField(activeField) && (
        <div className="field-input-overlay" onClick={() => setActiveField(null)}>
          <div className="sig-modal" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="sig-modal-header">
              <h3>{normalizeSignatureLikeType(activeField?.field_type) === 'initials' ? 'Add Initials' : 'Add Signature'}</h3>
              <button className="close-modal-btn" onClick={() => setActiveField(null)}>&times;</button>
            </div>

            {/* Tabs */}
            <div className="sig-tabs">
              <button className={`sig-tab${sigTab === 'draw' ? ' active' : ''}`} onClick={() => setSigTab('draw')}>
                <span className="sig-tab-icon">&#9999;&#65039;</span> Draw
              </button>
              <button className={`sig-tab${sigTab === 'upload' ? ' active' : ''}`} onClick={() => setSigTab('upload')}>
                <span className="sig-tab-icon">&#128194;</span> Upload/Existing
              </button>
              <button
                className={`sig-tab${sigTab === 'adopt' ? ' active' : ''}`}
                onClick={() => {
                  setSigTab('adopt');
                  if (!adoptStyles.length && adoptName.trim()) {
                    generateAdoptStyles(
                      adoptName,
                      normalizeSignatureLikeType(activeField?.field_type) === 'initials'
                    );
                  }
                }}
              >
                <span className="sig-tab-icon">&#127912;</span> Adopt
              </button>
            </div>

            {/* ── Tab: Draw ── */}
            {sigTab === 'draw' && (
              <div className="sig-tab-content">
                <div className="signature-canvas-wrapper">
                  <canvas
                    ref={signatureCanvasRef}
                    width={420}
                    height={160}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                  />
                  <button className="clear-canvas-btn" onClick={clearCanvas}>Clear</button>
                </div>
                <div className="modal-actions">
                  <button className="btn-cancel" onClick={clearCanvas}>Clear</button>
                  <button
                    className="btn-apply"
                    onClick={() => {
                      const dataUrl = getCanvasDataUrl();
                      if (dataUrl) applyFieldValue(activeField.id, dataUrl, 'draw');
                    }}
                  >
                    Save Signature
                  </button>
                </div>
              </div>
            )}

            {/* ── Tab: Upload / Existing ── */}
            {sigTab === 'upload' && (
              <div className="sig-tab-content">
                <div className="sig-upload-section">
                  <h4>Upload Signature</h4>
                  <input type="file" accept="image/*" onChange={handleSigUpload} className="sig-file-input" />
                  {uploadedSigDataUrl && (
                    <div className="sig-upload-preview">
                      <img src={uploadedSigDataUrl} alt="Uploaded signature" />
                      <button
                        className="btn-apply"
                        onClick={() => applyFieldValue(activeField.id, uploadedSigDataUrl, 'upload')}
                      >
                        Use This Signature
                      </button>
                    </div>
                  )}
                </div>

                <div className="sig-existing-section">
                  <h4>Existing Signatures</h4>
                  {existingSignatures.length > 0 ? (
                    <div className="sig-existing-grid">
                      {existingSignatures.map((sig) => (
                        <div
                          key={sig.id}
                          className="sig-existing-item"
                          onClick={() => applyFieldValue(activeField.id, sig.image_url, 'existing')}
                        >
                          <img src={sig.image_url} alt={sig.label || 'Saved signature'} />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="sig-no-existing">No saved signatures yet. Create one by drawing or adopting a signature style.</p>
                  )}
                </div>
              </div>
            )}

            {/* ── Tab: Adopt ── */}
            {sigTab === 'adopt' && (
              <div className="sig-tab-content">
                <div className="sig-adopt-section">
                  <h4>
                    {normalizeSignatureLikeType(activeField?.field_type) === 'initials'
                      ? 'Generate Initials from Name'
                      : 'Generate Signature from Name'}
                  </h4>
                  <p className="sig-adopt-hint">
                    {normalizeSignatureLikeType(activeField?.field_type) === 'initials'
                      ? 'Enter your name to generate initials options:'
                      : 'Enter your name to generate signature options:'}
                  </p>
                  <input
                    type="text"
                    className="sig-adopt-input"
                    placeholder={normalizeSignatureLikeType(activeField?.field_type) === 'initials' ? 'Your name or initials' : 'Your full name'}
                    value={adoptName}
                    onChange={(e) => setAdoptName(e.target.value)}
                  />
                  <button
                    className="btn-generate"
                    onClick={() => generateAdoptStyles(
                      adoptName,
                      normalizeSignatureLikeType(activeField?.field_type) === 'initials'
                    )}
                    disabled={!adoptName.trim()}
                  >
                    Generate Options
                  </button>
                </div>

                {adoptStyles.length > 0 && (
                  <div className="sig-adopt-styles">
                    <h4>Choose Your Signature Style</h4>
                    <div className="sig-adopt-grid">
                      {adoptStyles.map((style, idx) => (
                        <div
                          key={idx}
                          className={`sig-adopt-card${selectedAdopt === idx ? ' selected' : ''}`}
                          onClick={() => setSelectedAdopt(idx)}
                        >
                          <img src={style.dataUrl} alt={style.label} />
                          <span className="sig-adopt-label"><strong>{style.label}</strong></span>
                          <span className="sig-adopt-cta">Click to select</span>
                        </div>
                      ))}
                    </div>
                    {selectedAdopt !== null && (
                      <div className="modal-actions" style={{ marginTop: '1rem' }}>
                        <button
                          className="btn-apply"
                          onClick={() => applyFieldValue(activeField.id, adoptStyles[selectedAdopt].dataUrl, 'adopt')}
                        >
                          {normalizeSignatureLikeType(activeField?.field_type) === 'initials'
                            ? 'Use Selected Initials'
                            : 'Use Selected Signature'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <Modal
        show={!!signatureChoiceField}
        onHide={() => {
          setSignatureChoiceField(null);
          setSuggestedSignatureValue('');
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{normalizeSignatureLikeType(signatureChoiceField?.field_type) === 'initials' ? 'Confirm Initials' : 'Confirm Signature'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ marginBottom: '0.75rem' }}>
            You already added {normalizeSignatureLikeType(signatureChoiceField?.field_type) === 'initials' ? 'initials' : 'a signature'} in this session.
            For consistency, remaining {normalizeSignatureLikeType(signatureChoiceField?.field_type) === 'initials' ? 'initials' : 'signature'} fields use the same one.
            If you need a different {normalizeSignatureLikeType(signatureChoiceField?.field_type) === 'initials' ? 'initials style' : 'signature'}, reload the page and start again.
          </p>
          {suggestedSignatureValue ? (
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, background: '#fff' }}>
              <img
                src={suggestedSignatureValue}
                alt="Previous signature"
                style={{ maxWidth: '100%', maxHeight: 80, objectFit: 'contain', display: 'block', margin: '0 auto' }}
              />
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn-cancel"
            onClick={() => {
              setSignatureChoiceField(null);
              setSuggestedSignatureValue('');
            }}
          >
            Cancel
          </button>
          <button type="button" className="btn-apply" onClick={handleAdoptPreviousSignature}>
            Confirm
          </button>
        </Modal.Footer>
      </Modal>

      {/* ── Decline modal ───────────────────────────────── */}
      <Modal show={showDecline} onHide={() => setShowDecline(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Decline to Sign</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">Are you sure you want to decline signing this document? This action cannot be undone.</p>
          <textarea
            className="form-control"
            rows={3}
            placeholder="Reason for declining (optional)"
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-light" onClick={() => setShowDecline(false)}>Cancel</button>
          <button className="btn btn-danger" onClick={handleDecline} disabled={declining}>
            {declining ? <Spinner animation="border" size="sm" className="me-2" /> : null}
            Decline
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SignDocument;
